import React, { useEffect, useState } from "react";
import { Plus, X, Pencil, CheckSquare, User, UserMinus } from "lucide-react";
import { toast }            from "../../../ui/Toast";
import { useConfirmDelete } from "../../../../hooks/useConfirmDelete";

const API     = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card    = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls= "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls= "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const TYPES       = ["Onboarding","Offboarding","Probation Review","Role Change","Other"];
const OWNER_ROLES = ["HR","IT","Finance","Manager","Employee","Facilities","Legal"];

const DEFAULT_ONBOARDING = [
  { task: "Send offer letter and welcome email",     owner: "HR",       day_offset: -7  },
  { task: "Set up company email and accounts",       owner: "IT",       day_offset: -3  },
  { task: "Prepare workstation / equipment",         owner: "IT",       day_offset: -1  },
  { task: "Complete ID verification and right to work", owner: "HR",    day_offset: 0   },
  { task: "Sign employment contract",                owner: "HR",       day_offset: 0   },
  { task: "Induction and company overview",          owner: "HR",       day_offset: 0   },
  { task: "Meet team and manager",                   owner: "Manager",  day_offset: 0   },
  { task: "IT security training",                    owner: "IT",       day_offset: 1   },
  { task: "Add to payroll",                          owner: "Finance",  day_offset: 1   },
  { task: "Set up employee portal access",           owner: "HR",       day_offset: 1   },
  { task: "30-day check-in",                         owner: "Manager",  day_offset: 30  },
  { task: "90-day probation review",                 owner: "Manager",  day_offset: 90  },
];

const DEFAULT_OFFBOARDING = [
  { task: "Resignation/termination letter issued",   owner: "HR",       day_offset: 0   },
  { task: "Handover plan agreed with manager",       owner: "Manager",  day_offset: 0   },
  { task: "Return company equipment",                owner: "IT",       day_offset: -1  },
  { task: "Revoke system access",                    owner: "IT",       day_offset: -1  },
  { task: "Final payroll calculation",               owner: "Finance",  day_offset: -2  },
  { task: "Settle outstanding expenses",             owner: "Finance",  day_offset: -2  },
  { task: "Exit interview",                          owner: "HR",       day_offset: -3  },
  { task: "Issue relieving and experience letter",   owner: "HR",       day_offset: -1  },
  { task: "Remove from org systems and directories", owner: "IT",       day_offset: 0   },
  { task: "Final salary disbursement",               owner: "Finance",  day_offset: 5   },
];

export default function ChecklistsTab() {
  const [checklists, setChecklists] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [editing,    setEditing]    = useState(null);
  const [showForm,   setShowForm]   = useState(false);
  const [expanded,   setExpanded]   = useState(null);
  const [draft,      setDraft]      = useState({ name: "", type: "Onboarding", description: "", items: [] });
  const [saving,     setSaving]     = useState(false);
  const [msg,        setMsg]        = useState(null);
  const { confirmDelete, ConfirmDialog } = useConfirmDelete();
  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/checklists`, { headers: { Authorization: `Bearer ${token}` } });
      setChecklists(Array.isArray(await res.json()) ? await res.json() : []);
    } catch { setChecklists([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadDefault = (type) => {
    const items = type === "Onboarding" ? DEFAULT_ONBOARDING : type === "Offboarding" ? DEFAULT_OFFBOARDING : [];
    setDraft(d => ({ ...d, type, items: items.map(i => ({ ...i, required: true })) }));
  };

  const save = async () => {
    if (!draft.name) return;
    setSaving(true); setMsg(null);
    try {
      const isNew = !editing;
      const res = await fetch(
        isNew ? `${API}/api/admin/checklists` : `${API}/api/admin/checklists/${editing}`,
        { method: isNew ? "POST" : "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(draft) }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg({ type: "success", text: "✓ Checklist saved." });
      setShowForm(false); setDraft({ name: "", type: "Onboarding", description: "", items: [] }); setEditing(null);
      await load();
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setSaving(false); }
  };

  const del = (id) => confirmDelete("this checklist", async () => {
    try {
      const res = await fetch(`${API}/api/admin/checklists/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setChecklists(c => c.filter(x => x.id !== id));
      toast.success("Checklist deleted");
    } catch {
      toast.error("Failed to delete checklist");
    }
  });

  const startEdit = (c) => { setDraft({ ...c }); setEditing(c.id); setShowForm(true); };

  const addItem   = () => setDraft(d => ({ ...d, items: [...d.items, { task: "", owner: "HR", day_offset: 0, required: true }] }));
  const remItem   = (i) => setDraft(d => ({ ...d, items: d.items.filter((_, xi) => xi !== i) }));
  const setItem   = (i, k, v) => setDraft(d => ({ ...d, items: d.items.map((x, xi) => xi === i ? { ...x, [k]: v } : x) }));

  return (
    <div className="space-y-5">

      {/* Form */}
      {showForm && (
        <div className={`${card} p-5 space-y-4`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
            {editing ? "Edit Checklist" : "New Checklist"}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Checklist Name</label>
              <input className={inputCls} value={draft.name} onChange={e => setDraft(d => ({...d, name: e.target.value}))} placeholder="e.g. Standard Onboarding" />
            </div>
            <div>
              <label className={labelCls}>Type</label>
              <select className={inputCls} value={draft.type} onChange={e => { setDraft(d => ({...d, type: e.target.value})); }}>
                {TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Description</label>
              <input className={inputCls} value={draft.description || ""} onChange={e => setDraft(d => ({...d, description: e.target.value}))} />
            </div>
          </div>

          {/* Load defaults */}
          {(draft.type === "Onboarding" || draft.type === "Offboarding") && draft.items.length === 0 && (
            <button onClick={() => loadDefault(draft.type)}
              className="text-xs px-4 py-2 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
              Load default {draft.type} checklist
            </button>
          )}

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider">Checklist Items</p>
              <button onClick={addItem}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                <Plus size={12} /> Add Item
              </button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {draft.items.map((item, i) => (
                <div key={i} className="grid grid-cols-5 gap-2 items-center rounded-lg border border-white/5 bg-white/3 px-3 py-2">
                  <div className="col-span-2">
                    <input className={inputCls} value={item.task} onChange={e => setItem(i, "task", e.target.value)} placeholder="Task description" />
                  </div>
                  <div>
                    <select className={inputCls} value={item.owner} onChange={e => setItem(i, "owner", e.target.value)}>
                      {OWNER_ROLES.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <input type="number" className={inputCls} value={item.day_offset ?? 0}
                      onChange={e => setItem(i, "day_offset", Number(e.target.value))}
                      title="Day offset from joining/leaving date (negative = before)" />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <label className="flex items-center gap-1 text-xs text-white/40 cursor-pointer">
                      <input type="checkbox" checked={!!item.required} onChange={e => setItem(i, "required", e.target.checked)} className="w-3 h-3 accent-[#00ff90]" />
                      Req
                    </label>
                    <button onClick={() => remItem(i)} className="text-white/20 hover:text-red-400 transition-colors"><X size={13} /></button>
                  </div>
                </div>
              ))}
              {draft.items.length === 0 && <p className="text-white/20 text-xs italic text-center py-3">No items yet.</p>}
            </div>
            <p className="text-xs text-white/20 mt-1.5">Day offset: negative = before start/end date, positive = after</p>
          </div>

          {msg && <p className={`text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}
          <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
            <button onClick={() => { setShowForm(false); setDraft({ name: "", type: "Onboarding", description: "", items: [] }); setEditing(null); }}
              className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={save} disabled={saving || !draft.name}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Saving…" : "Save Checklist"}
            </button>
          </div>
        </div>
      )}

      {/* Checklist list */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Checklist Templates</span>
          <button onClick={() => { setDraft({ name: "", type: "Onboarding", description: "", items: [] }); setEditing(null); setShowForm(v => !v); }}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={12} /> New Checklist
          </button>
        </div>
        <div className="divide-y divide-white/5">
          {loading ? Array.from({length:3}).map((_,i) => <div key={i} className="px-5 py-4"><div className="h-10 rounded-lg bg-white/5 animate-pulse" /></div>)
          : checklists.length === 0 ? <p className="px-5 py-8 text-white/20 text-sm italic text-center">No checklists yet.</p>
          : checklists.map(c => (
            <div key={c.id}>
              <div className="flex items-center justify-between gap-3 px-5 py-4 hover:bg-white/3 transition-colors cursor-pointer"
                onClick={() => setExpanded(expanded === c.id ? null : c.id)}>
                <div className="flex items-center gap-3">
                  {c.type === "Onboarding" ? <User size={14} className="text-[#00ff90]/50" /> : <UserMinus size={14} className="text-red-400/50" />}
                  <div>
                    <p className="text-sm text-white/75 font-medium">{c.name}</p>
                    <p className="text-xs text-white/30">{c.type} · {(c.items||[]).length} items</p>
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={e => { e.stopPropagation(); startEdit(c); }}
                    className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                    <Pencil size={12} />
                  </button>
                  <button onClick={e => { e.stopPropagation(); del(c.id); }}
                    className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                    <X size={12} />
                  </button>
                </div>
              </div>
              {expanded === c.id && (
                <div className="px-5 pb-4 space-y-1">
                  {(c.items||[]).map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs rounded-lg border border-white/5 bg-white/3 px-3 py-2">
                      <CheckSquare size={11} className="text-[#00ff90]/40 shrink-0" />
                      <span className="flex-1 text-white/60">{item.task}</span>
                      <span className="text-[#00ff90]/40">{item.owner}</span>
                      <span className="text-white/25">Day {item.day_offset >= 0 ? `+${item.day_offset}` : item.day_offset}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {ConfirmDialog}
    </div>
  );
}